<template>
  <v-autocomplete
    v-model:search="query"
    auto-select-first
    :custom-filter="filterUser"
    :disabled="field.readonly"
    :error-messages="errors"
    :hide-details="dense"
    item-value="id"
    :items="users"
    :loading="usersLoading"
    menu-icon="$expand"
    :model-value="modelValue"
    :multiple="field.multiple"
    :rules="rules"
    @update:menu="onMenuChange($event)"
    @update:model-value="onUpdateModelValue($event)"
  >
    <template #label>
      <FieldLabel :field="field" />
    </template>

    <template #item="{ props: slotProps, item }">
      <v-list-item
        v-if="showUserInList(item.raw)"
        v-bind="slotProps"
      >
        <template #title>
          <QueryHighlight
            :query="query"
            :text="getUserDisplayName(item.raw, true)"
          />
        </template>

        <template #subtitle>
          <QueryHighlight
            :query="query"
            :text="item.raw.email"
          />
        </template>

        <template #prepend>
          <UserAvatar
            :size="28"
            :user="{ name: `${item.raw.first_name} ${item.raw.last_name}` }"
          />
        </template>
      </v-list-item>
    </template>

    <template #chip="{ props: slotProps, item }">
      <UserChip
        dismissable
        :user="item.raw"
        v-bind="slotProps"
        @click:dismiss="removeUser(item.raw)"
      />
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
const props = defineProps<{
  field: FieldConfig
  modelValue: number | number[]
  errors?: string[]
  dense?: boolean
  rules?: FormRule[]
}>();
const emit = defineEmits(['update:modelValue']);
const modelValue = toRef(props, 'modelValue');
const query = ref('');

const usersStore = useUsersStore();
const { fetchUsers } = usersStore;
const { users, usersLoading } = storeToRefs(usersStore);

/**
 * Given a query string and an item from the list, determines if
 * the item should be displayed in the list of options for the field
 *
 * @param _ The title of the item, unused argument
 * @param query
 * @param item
 */
const filterUser = (_: string, query: string, item: any) => {
  const user: User | UserWithRoles = item.raw;
  if (Array.isArray(modelValue.value) && modelValue.value.includes(user.id)) {
    return false;
  }
  if (!query) {
    return true;
  }
  const name = `${user.first_name} ${user.last_name}`.toLowerCase();
  const email = user.email.toLowerCase();
  const queryLower = query.toLowerCase();
  return name.includes(queryLower) || email.includes(queryLower);
};

// TODO: create function to return HTML with matching text wrapped in span

/**
 * Determines whether or not a user should be shown in the list of options
 * or fitered out and hidden.
 *
 * @param user
 */
const showUserInList = (user: User | UserWithRoles) => {
  if (typeof modelValue.value === 'undefined') {
    return true;
  }
  return (Array.isArray(modelValue.value) && !modelValue.value.includes(user.id));
};

/**
 * Re-fetches the list of users whenever the menu is opened
 *
 * @param menuOpen
 */
const onMenuChange = async (menuOpen: boolean) => {
  if (menuOpen) {
    fetchUsers({
      page: 1,
      itemsPerPage: 100,
      sortBy: [{ key: 'email', order: 'asc' }],
      ...(props.field.filters || {}),
    });
  }
};

/**
 * Whenever the model value is updated, checks to see if an
 * item was added (if the model value is an array).
 * If so, the query string is cleared out.
 *
 * @param newValue
 */
const onUpdateModelValue = (newValue: number | number[]) => {
  if (
    Array.isArray(newValue) &&
    Array.isArray(modelValue.value) &&
    newValue.length > modelValue.value.length
  ) {
    query.value = '';
  }
  emit('update:modelValue', newValue);
};

/**
 * Removes an item from the selected list, updating the modelValue.
 *
 * @param item
 */
const removeUser = (user: User | UserWithRoles) => {
  if (!Array.isArray(modelValue.value)) {
    return;
  }
  const existing = modelValue.value.slice();
  const idIndex = existing.indexOf(user.id);
  existing.splice(idIndex, 1);
  emit('update:modelValue', existing);
};
</script>
